/**
 * Node-safe provider theme styles data.
 *
 * This file contains only pure data (no SVG imports, no browser dependencies)
 * so it can be safely required at build time by yoshi-flow-editor's
 * builder params resolution (which runs in Node.js).
 *
 * The full PROVIDER_THEME_CONFIG (with icons) is in providerThemeConfig.ts.
 */
import { ProviderColorTheme } from '../SocialButtonsOptions/constants';
import { SocialButtonClassName } from './SocialButton.types';
import { IDP_APPDEFIDS } from '../../../constants';
import { wixFontParam } from '@wix/tpa-settings';

// Helper function to create font objects
const font = (fontKey: string, styleOverrides?: any) =>
  wixFontParam(fontKey as any, styleOverrides);

export interface ThemeConfig {
  className: SocialButtonClassName;
  styles: {
    textColor: { value: string; name: string | null; opacity: number };
    hoverTextColor?: { value: string; name: string | null; opacity: number };
    buttonHoverTextFont?: any; // Font objects from wixFontParam
    backgroundColor: { value: string; name: string | null; opacity: number };
    hoverBackgroundColor: {
      value: string;
      name: string | null;
      opacity: number;
    };
    borderColor: { value: string; name: string | null; opacity: number };
    hoverBorderColor?: { value: string; name: string | null; opacity: number };
    borderWidth: number;
    borderRadius: number;
  };
}

/** Theme styles per provider (without icons). */
export const PROVIDER_THEME_STYLES: Record<
  string,
  {
    themeParam: string;
    themes: Record<ProviderColorTheme, ThemeConfig>;
  }
> = {
  [IDP_APPDEFIDS.google]: {
    themeParam: 'googleColorTheme',
    themes: {
      [ProviderColorTheme.OUTLINE]: {
        className: 'google',
        styles: {
          textColor: { value: '#1F1F1F', name: null, opacity: 1 },
          backgroundColor: { value: '#FFFFFF', name: null, opacity: 1 },
          hoverBackgroundColor: { value: '#EEEEEE', name: null, opacity: 1 },
          borderColor: { value: '#747775', name: null, opacity: 1 },
          borderWidth: 1,
          borderRadius: 0,
        },
      },
      [ProviderColorTheme.LIGHT]: {
        className: 'google',
        styles: {
          textColor: { value: '#1F1F1F', name: null, opacity: 1 },
          backgroundColor: { value: '#F2F2F2', name: null, opacity: 1 },
          hoverBackgroundColor: { value: '#DFE0E2', name: null, opacity: 1 },
          borderColor: { value: '#000000', name: null, opacity: 0 },
          borderWidth: 0,
          borderRadius: 0,
        },
      },
      [ProviderColorTheme.DARK]: {
        className: 'google',
        styles: {
          textColor: { value: '#E3E3E3', name: null, opacity: 1 },
          backgroundColor: { value: '#131314', name: null, opacity: 1 },
          hoverBackgroundColor: { value: '#262626', name: null, opacity: 1 },
          borderColor: { value: '#8E918F', name: null, opacity: 1 },
          borderWidth: 1,
          borderRadius: 0,
        },
      },
    },
  },
  [IDP_APPDEFIDS.facebook]: {
    themeParam: 'facebookColorTheme',
    themes: {
      [ProviderColorTheme.OUTLINE]: {
        className: 'facebook',
        styles: {
          textColor: { value: '#1F1F1F', name: null, opacity: 1 },
          backgroundColor: { value: '#FFFFFF', name: null, opacity: 1 },
          hoverBackgroundColor: { value: '#EEEEEE', name: null, opacity: 1 },
          borderColor: { value: '#747775', name: null, opacity: 1 },
          borderWidth: 1,
          borderRadius: 0,
        },
      },
      [ProviderColorTheme.LIGHT]: {
        className: 'facebook',
        styles: {
          textColor: { value: '#FFFFFF', name: null, opacity: 1 },
          backgroundColor: { value: '#1877F2', name: null, opacity: 1 },
          hoverBackgroundColor: { value: '#166FE5', name: null, opacity: 1 },
          borderColor: { value: '#000000', name: null, opacity: 0 },
          borderWidth: 0,
          borderRadius: 0,
        },
      },
      [ProviderColorTheme.DARK]: {
        className: 'facebook',
        styles: {
          textColor: { value: '#E3E3E3', name: null, opacity: 1 },
          backgroundColor: { value: '#131314', name: null, opacity: 1 },
          hoverBackgroundColor: { value: '#262626', name: null, opacity: 1 },
          borderColor: { value: '#8E918F', name: null, opacity: 1 },
          borderWidth: 1,
          borderRadius: 0,
        },
      },
    },
  },
  [IDP_APPDEFIDS.sso]: {
    themeParam: 'ssoColorTheme',
    themes: {
      [ProviderColorTheme.OUTLINE]: {
        className: 'sso',
        styles: {
          textColor: { value: '#1F1F1F', name: null, opacity: 1 },
          backgroundColor: { value: '#FFFFFF', name: null, opacity: 1 },
          hoverBackgroundColor: { value: '#EEEEEE', name: null, opacity: 1 },
          borderColor: { value: '#747775', name: null, opacity: 1 },
          borderWidth: 1,
          borderRadius: 0,
          hoverTextColor: { value: '#1F1F1F', name: null, opacity: 1 },
          buttonHoverTextFont: font('Body', { size: 16 }),
          hoverBorderColor: { value: '#747775', name: null, opacity: 1 },
        },
      },
      [ProviderColorTheme.LIGHT]: {
        className: 'sso',
        styles: {
          textColor: { value: '#1F1F1F', name: null, opacity: 1 },
          backgroundColor: { value: '#F2F2F2', name: null, opacity: 1 },
          hoverBackgroundColor: { value: '#DFE0E2', name: null, opacity: 1 },
          borderColor: { value: '#000000', name: null, opacity: 0 },
          borderWidth: 0,
          borderRadius: 0,
          hoverTextColor: { value: '#1F1F1F', name: null, opacity: 1 },
          buttonHoverTextFont: font('Body', { size: 16 }),
          hoverBorderColor: { value: '#000000', name: null, opacity: 0 },
        },
      },
      [ProviderColorTheme.DARK]: {
        className: 'sso',
        styles: {
          textColor: { value: '#E3E3E3', name: null, opacity: 1 },
          backgroundColor: { value: '#131314', name: null, opacity: 1 },
          hoverBackgroundColor: { value: '#262626', name: null, opacity: 1 },
          borderColor: { value: '#8E918F', name: null, opacity: 1 },
          borderWidth: 1,
          borderRadius: 0,
          hoverTextColor: { value: '#E3E3E3', name: null, opacity: 1 },
          buttonHoverTextFont: font('Body', { size: 16 }),
          hoverBorderColor: { value: '#8E918F', name: null, opacity: 1 },
        },
      },
    },
  },
};

// Utility function to get theme styles from theme and provider ID
export function getThemeStyles(
  theme: ProviderColorTheme,
  providerId: string,
): ThemeConfig['styles'] | null;
export function getThemeStyles<K extends keyof ThemeConfig['styles']>(
  theme: ProviderColorTheme,
  providerId: string,
  styleProperty: K,
): ThemeConfig['styles'][K] | null;
export function getThemeStyles<K extends keyof ThemeConfig['styles']>(
  theme: ProviderColorTheme,
  providerId: string,
  styleProperty?: K,
): ThemeConfig['styles'] | ThemeConfig['styles'][K] | null {
  const config = PROVIDER_THEME_STYLES[providerId];
  const themeStyles = config?.themes[theme]?.styles;

  if (!themeStyles) {
    return null;
  }

  if (styleProperty !== undefined) {
    return themeStyles[styleProperty];
  }

  return themeStyles;
}
